/* The service client. One function per route, and no opinion about the report.
 *
 * Ported from `client/src/lib/api.ts`, which is the tested wire logic. Nothing
 * here reshapes, renames or fills in a field: the service returns its pipeline
 * output verbatim and this file hands that object straight on.
 *
 * **Same origin, always.** The dev server proxies `/v1` to the service, so
 * every URL below is a path and no request carries a cross-origin preflight.
 * The old client read a `VITE_API_BASE` env var here; this one does not, so no
 * module in `src/lib` needs Vite's ambient types and the pure ones compile
 * under Node for the unit tests.
 *
 * **Two steps rather than one, on purpose.** `inspect` normalises an upload and
 * returns a manifest without reading anything, because a sixteen-page PDF is
 * sixteen vision calls and a parent should be told before that is spent.
 * `submit` then reads. The service caches rendered pages by content hash, so
 * the second call does not redo the rasterising.
 *
 * **Two headers, and each goes on its own half of the app.** `X-Learner` is the
 * learner capability and travels with pages, reports, papers and the thread.
 * `X-Session` is the parent's account bearer token and travels only under
 * `/v1/account` and, since wave 2, `/v1/admin`. That is enforced by shape:
 * `sessioned()` is the only thing that writes the session header and nothing
 * on the learner half of this file calls it. `Authorization` is never sent.
 */
import type { Capture } from "./capture";
import { ServiceError, UNREACHED, isTransient } from "./errors";
import { outcomeOf } from "./outcome";
import { track } from "./telemetry";
import type {
  Account, AccountDeleted, AdminOverview, Attention, Confirmed, Entitlement,
  Invite, InviteSend, Issued, Manifest, PageState, PaperState, QueueEntry,
  Report, SettingRow, Started, Stored, Thread, Waiting,
} from "./types";

export { ServiceError, UNREACHED, isTransient };

/** Every route is a path. See the header: the dev server proxies `/v1`. */
const BASE = "";

/** Learning history uses the same owner capability and error contract as reports. */
export async function learningRequest<T>(sha: string, child: string, suffix = "", body?: unknown): Promise<T> {
  const res = await ask(`/v1/learning/${encodeURIComponent(sha)}${suffix}`, {
    method: body === undefined ? "GET" : (suffix === "/activity" || suffix === "/resource") ? "PUT" : "POST",
    headers: { "X-Learner": child, "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) throw await refusal(res);
  return await res.json() as T;
}

export interface PracticeSkill {
  title: string; sha: string; last_at: number; attempted: number;
  independent_right: number; helped: number; later_checks: number; later_right: number;
}
export async function practiceHistory(child: string): Promise<PracticeSkill[]> {
  const res = await ask('/v1/practice-history', { headers: { 'X-Learner': child } });
  if (!res.ok) throw await refusal(res);
  return (await res.json() as { skills: PracticeSkill[] }).skills;
}

export async function learningAdmin<T>(token: string, suffix = "", body?: unknown): Promise<T> {
  const res = await ask(`/v1/admin/learning${suffix}`, {
    method: body === undefined ? "GET" : "POST",
    headers: { "X-Session": token, "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) throw await refusal(res);
  return await res.json() as T;
}

export async function modelQuality<T>(token: string): Promise<T> {
  const res = await ask('/v1/admin/model-quality', { headers: { 'X-Session': token } });
  if (!res.ok) throw await refusal(res);
  return await res.json() as T;
}

/** Turn a refusal into one of ours, carrying whatever the service said about
 *  when to come back. The message is never rendered: `say.ts` writes what a
 *  parent reads, from the status. */
async function refusal(res: Response): Promise<ServiceError> {
  const body = await res.json().catch(() => null);
  /* Agent V5-C, wave 2. The one refusal in the product, counted where it is
     built rather than in each of the screens that draws it. A 402 and the
     spend cap's 503 both go straight to the refusal screen, so a refusal
     reaching this line is a refusal a parent is about to read. Nothing about
     the page, the child or the body goes with it: a kind, and that is all. */
  if (res.status === 402 || res.status === 503) {
    const said = (body as { error?: string; reason?: string } | null);
    const reason = said?.error === "paused" || said?.reason === "paused"
      ? "paused"
      : said?.reason === "cap" ? "cap" : res.status === 402 ? "credits" : "service";
    track("refusal shown", { reason, status: res.status });
  }
  const header = Number(res.headers.get("retry-after"));
  const stated = (body as { retry_after?: number } | null)?.retry_after;
  const wait = Number.isFinite(header) && header > 0
    ? header
    : typeof stated === "number" && stated > 0 ? stated : undefined;
  return new ServiceError(
    (body as { detail?: string } | null)?.detail ?? `service returned ${res.status}`,
    res.status, body, wait);
}

/* Nothing in this client may throw a string a browser wrote.
 *
 * `fetch` rejects with a TypeError whose message is the browser's own: "Failed
 * to fetch" on Chrome, "Load failed" on Safari, "NetworkError when attempting
 * to fetch resource." on Firefox. Those three strings once reached a failure
 * screen as its heading. Every request in this file goes through `ask`, so a
 * connection that drops arrives as a ServiceError like any other and `say.ts`
 * decides what a parent is told.
 */
async function ask(url: string, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch {
    throw new ServiceError("the request did not reach the service", UNREACHED);
  }
}

function learner(childId?: string): RequestInit | undefined {
  return childId ? { headers: { "X-Learner": childId } } : undefined;
}

/* ------------------------------------------------------------------ learners */

/** Ask the service for a learner id.
 *
 *  The id used to be minted on the device, and the write side took whatever
 *  arrived, so the free allowance was keyed on a string anyone could invent: a
 *  fresh id per request bought a fresh allowance per request. The service
 *  issues and signs them now.
 *
 *  `migrate` carries an existing device-minted id across once. The service
 *  refuses a second attempt on the same old id with a 409, which is what stops
 *  one legacy id becoming two allowances.
 */
export async function issueLearner(migrate?: string): Promise<Issued> {
  const res = await ask(`${BASE}/v1/learners`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(migrate ? { migrate } : {}),
  });
  if (res.status !== 201) throw await refusal(res);
  return (await res.json()) as Issued;
}

/** Remove everything for one learner. The id is sent twice, in the path and in
 *  the header, because the service will not act on a DELETE that erases
 *  whatever the header happens to be carrying. */
export async function eraseLearner(childId: string): Promise<boolean> {
  const res = await ask(`${BASE}/v1/learners/${encodeURIComponent(childId)}`,
    { method: "DELETE", headers: { "X-Learner": childId } });
  if (res.status === 404) return false;
  if (!res.ok) throw await refusal(res);
  return true;
}

/* --------------------------------------------------------------- entitlement */

/** Whether a body is an entitlement at all.
 *
 *  **Not `pages_included`.** That field was the proof of shape until the
 *  service settled what it means in credits mode: it now equals
 *  `credits.granted`, and before that it answered 5 beside a grant of 3, so a
 *  screen that ever printed it printed a number the meter did not honour. A
 *  field whose value was wrong is a poor thing to key existence on. `entitled`
 *  and `pages_used` are on every entitlement the service has ever sent, on
 *  both meters, and neither is a number about money. */
function isEntitlement(ent: Entitlement | null | undefined): ent is Entitlement {
  return typeof ent?.entitled === "boolean"
    && typeof ent?.pages_used === "number";
}

/** What a page costs, asked before there is anything to spend it on.
 *
 *  Every other route that answers with an entitlement needs something first: a
 *  file for the dry run, a hash for a stored report, a learner for the thread.
 *  The landing screen has none of the three.
 *
 *  **It fails soft, deliberately.** Anything other than a 200 returns null and
 *  the caller says nothing about cost. A service that has not shipped the route
 *  answers 404 and loses nothing; a connection that is not there loses nothing
 *  either. Nothing on this path may sit between a parent and the camera. */
export async function entitlement(childId?: string): Promise<Entitlement | null> {
  try {
    const res = await ask(`${BASE}/v1/entitlement`, learner(childId));
    if (!res.ok) return null;
    const body = (await res.json()) as
      { entitlement?: Entitlement; sample?: Entitlement["sample"] };
    const ent = body?.entitlement ?? (body as unknown as Entitlement);
    if (!isEntitlement(ent)) return null;
    /* **`sample` is a SIBLING of `entitlement` on the wire, not a field on
       it.** The app's type carries it on the entitlement because that is where
       every screen wants it, and this is the one line that puts it there. It
       was missing until the W2b walk: `landing/sample.ts` reads
       `ent.sample.count` and never saw one, so a device with no account was
       never told what its free sample was however the operator set the dial.
       Recorded off the service on 5 September 2026, which is how the shape was
       settled rather than guessed. */
    return body?.sample !== undefined && ent.sample === undefined
      ? { ...ent, sample: body.sample }
      : ent;
  } catch {
    return null;
  }
}

/* -------------------------------------------------- Agent V5-C, wave 2: the
 * host's own switches
 *
 * `GET /v1/config` takes no auth and carries no secret. It says whether there
 * is a PostHog project and where it is, whether there is a Sentry project,
 * whether sign-up is invitation only, and which partner links to show.
 *
 * **It fails soft, for the same reason `entitlement` does.** Anything but a
 * 200 answers null, and null means every one of those is off. A service that
 * has not shipped the route answers 404 and this app then tracks nothing,
 * reports nothing and shows no partner strip, which is exactly what a host
 * that has configured nothing should get.
 */
export async function config(): Promise<unknown | null> {
  try {
    const res = await ask(`${BASE}/v1/config`);
    if (!res.ok) return null;
    return (await res.json()) as unknown;
  } catch {
    return null;
  }
}

/* --------------------------------------------------------------------- pages */

export interface SubmitOptions {
  /** Explicit override. Normally absent: send `schoolYear` and let the service
   *  map it, so the year-to-key-stage table exists once and is gated once. */
  keyStages?: string[];
  /** As the parent typed it. "8", "Year 8", "S2" and "P7" are all handled. */
  schoolYear?: string;
  paperId?: string;
  childId?: string;
}

function form(caps: Capture[], opts: SubmitOptions, dryRun: boolean): FormData {
  const fd = new FormData();
  /* A fixed filename, never `cap.file.name`. The multipart part carries the
     filename in its Content-Disposition, and a photograph chosen from the
     library is very often called something like "sana-maths.jpg". The contract
     says the child's name reaches the server in no field, and this is a field.
     The service reads the bytes and the form values, never the name. */
  caps.forEach((cap, i) => fd.append("image", cap.file, `page-${i + 1}`));
  if (opts.keyStages?.length) fd.append("key_stages", opts.keyStages.join(","));
  if (opts.schoolYear) fd.append("school_year", opts.schoolYear);
  if (opts.paperId) fd.append("paper_id", opts.paperId);
  if (opts.childId) fd.append("child_id", opts.childId);
  if (dryRun) fd.append("dry_run", "true");
  return fd;
}

/* The Response rather than a parsed body, because a 429 says when to come back
 * in a header and parsing first throws that away.
 *
 * The learner goes in the FORM FIELD on this route. `POST /v1/pages` takes
 * `child_id` as a multipart field and reads no `X-Learner` at all, which is the
 * one place the wire differs from every other learner-scoped route. The header
 * is sent as well because it costs nothing same-origin and the contract asks
 * for it, but the field is the load-bearing half and a page sent without it
 * belongs to nobody. */
function post(fd: FormData, childId?: string): Promise<Response> {
  return ask(`${BASE}/v1/pages`, { method: "POST", body: fd, ...learner(childId) });
}

/** Normalise the upload and report what is in it. Spends nothing. */
export async function inspect(caps: Capture[],
                              opts: SubmitOptions = {}): Promise<Manifest> {
  const res = await post(form(caps, opts, true), opts.childId);
  if (res.status !== 200) throw await refusal(res);
  return (await res.json()) as Manifest;
}

/** Read them. One vision call per page that is not already cached.
 *
 *  A 402 arrives here as a ServiceError with the parsed body attached, and that
 *  body still carries `entitlement` and a `reason`, which is where the refusal
 *  screen gets its numbers and its sentence. */
export async function submit(caps: Capture[],
                             opts: SubmitOptions = {}): Promise<Started> {
  const res = await post(form(caps, opts, false), opts.childId);
  if (res.status !== 202) throw await refusal(res);
  track("page sent", { pages: caps.length });
  return (await res.json()) as Started;
}

/** One poll of a job. The learner header goes with it: the service answers 404
 *  to a claimed job polled without its owner. A job nobody claimed still
 *  answers without one. */
export async function getPage(jobId: string,
                              childId?: string): Promise<PageState> {
  const res = await ask(`${BASE}/v1/pages/${encodeURIComponent(jobId)}`,
    learner(childId));
  if (res.status === 404) throw new ServiceError("no such page", 404);
  const body = (await res.json()) as PageState;
  if (!res.ok && res.status !== 202) {
    throw new ServiceError(`service returned ${res.status}`, res.status, body);
  }
  return body;
}

/** Remove one page. True when it is gone, false when the service says there is
 *  no such page for this learner, which is also what a second tap on a control
 *  that already worked looks like. */
export async function deletePage(sha256: string,
                                 childId: string): Promise<boolean> {
  const res = await ask(`${BASE}/v1/pages/${encodeURIComponent(sha256)}`,
    { method: "DELETE", headers: { "X-Learner": childId } });
  if (res.status === 404) return false;
  if (!res.ok) throw await refusal(res);
  return true;
}

/* --------------------------------------------------------------------- typed
 *
 * A question a parent typed, because the photograph would not do it: a page
 * still in a schoolbag, a question read off a screen, handwriting a camera on a
 * kitchen table will not carry.
 *
 * **It answers a finished page, not a job.** There is no vision call to wait
 * on, so `POST /v1/typed` returns 200 with the same body a settled
 * `GET /v1/pages/{job}` returns, and the screen goes straight to the report
 * rather than through the reading screen.
 *
 * **It costs what a page costs.** One credit where the question was marked,
 * free where it was not, settled by the service in `billable` exactly as a
 * photograph is. A zero balance is refused with the same 402 body the upload
 * route sends, so `say()` writes the same sentence for both and there is one
 * refusal in the product rather than two.
 */

/** What is sent. The learner id and the year are what the service meters and
 *  marks against; the child's name is not here for the same reason it is not on
 *  an upload. */
export interface TypedAsk {
  childId: string;
  schoolYear?: string;
  question: string;
  answer: string;
  /** What was written on the way to the answer, where the parent had it. */
  working?: string;
}

/** What the typed route answered.
 *
 *  **`sha256` is read off the body and never computed here.** The service
 *  stores the report under the sha256 of its own normalised form of the typed
 *  text, and its normalisation is the only one that counts: a hash worked out
 *  on this side would agree until the day the service trims a space
 *  differently, and then it would send a parent to a page that does not exist.
 *  It sits beside `job_id` on the wire and the two are different strings.
 *
 *  `page` is whatever state came back. It is `done` on every answer the service
 *  has produced so far, and it is a union rather than a `PageDone` because a
 *  read that fails on the text is an error state like any other, and a client
 *  that could not represent one would have to invent a sentence for it. */
export interface TypedAnswer {
  /** Where the report is stored. Null where the answer produced no report and
   *  so there is nothing to come back to. */
  sha256: string | null;
  page: PageState;
}

export async function typed(req: TypedAsk): Promise<TypedAnswer> {
  const res = await ask(`${BASE}/v1/typed`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "X-Learner": req.childId,
    },
    body: JSON.stringify({
      child_id: req.childId,
      school_year: req.schoolYear,
      question: req.question,
      answer: req.answer,
      working: req.working,
    }),
  });
  if (!res.ok) throw await refusal(res);
  const body = (await res.json()) as PageState & { sha256?: string };
  const sha = typeof body?.sha256 === "string" && body.sha256 ? body.sha256 : null;
  if (body?.state === "done" || body?.state === "error") {
    track("typed question", { outcome: outcomeOf(body) });
  }
  if (body?.state === "done") {
    if (!sha) {
      /* Fail rather than fall back. There is no second handle for a stored
         report, and navigating to a job id would 404 on a report that exists,
         which reads to a parent as a page that was lost. */
      throw new ServiceError("the typed answer carried no sha256", res.status, body);
    }
    return { sha256: sha, page: body };
  }
  if (body?.state === "error") return { sha256: sha, page: body };
  throw new ServiceError("the typed question came back unfinished", res.status, body);
}

/* ------------------------------------------------------------------- reports */

/** The stored report for a page, by the sha256 of its bytes. Null when there is
 *  none, which is also what a page still being read looks like from here.
 *
 *  This is the handle that survives a service restart, so it is the fallback
 *  rather than the first ask: while a job is alive it knows whether the read is
 *  running, and this endpoint cannot tell "still reading" from "never read".
 *
 *  The learner header goes with it. The service wants it on any page a learner
 *  has claimed and answers 404 without it. */
export async function getReport(sha256: string,
                                childId?: string): Promise<Stored | null> {
  const res = await ask(`${BASE}/v1/reports/${encodeURIComponent(sha256)}`,
    learner(childId));
  if (res.status === 404 || res.status === 400) return null;
  if (!res.ok) throw new ServiceError(`service returned ${res.status}`, res.status);
  const body = (await res.json()) as {
    report?: Report; billable?: boolean | null; charged?: boolean | null;
    can_confirm?: boolean | null;
  };
  return body.report
    ? {
      report: body.report,
      billable: body.billable ?? null,
      charged: body.charged ?? null,
      /* Whether this learner may correct the reading, as the service settled
         it. Read off the envelope and not off the report, because it is a
         fact about who is asking and the report is the same body for every
         owner. Null when the service did not say. FP1-05, and the wire
         ruling of 5 September 2026: `can_confirm` is the field, on this
         route and on the done poll body. */
      can_confirm: body.can_confirm ?? null,
    }
    : null;
}

/** Type in what we could not read, and get the page re-marked.
 *
 *  The binding rule is that this never spends a credit: the vision payload is
 *  already stored beside the report, so the service re-runs the marking over a
 *  corrected reading and calls the model not at all. `spent` comes back 0 and
 *  the screen says so, because a parent told we could not read their child's
 *  work should not then wonder whether fixing it costs them.
 *
 *  The body is `{answers: {"Q1": "what it says"}}`, keyed on the question
 *  labels the report's `ask.questions` names. The service checks who is writing
 *  as well as who is reading: a correction re-marks a stored page and lands in
 *  the corpus, so a hash was never enough authorisation for it. */
export async function confirm(sha256: string, answers: Record<string, string>,
                              childId?: string, questionConfirmed: string[] = []): Promise<Confirmed> {
  const res = await ask(
    `${BASE}/v1/reports/${encodeURIComponent(sha256)}/confirm`,
    {
      method: "POST",
      headers: {
        "content-type": "application/json",
        ...(childId ? { "X-Learner": childId } : {}),
      },
      body: JSON.stringify({ answers, question_confirmed: questionConfirmed }),
    });
  if (!res.ok) throw await refusal(res);
  return (await res.json()) as Confirmed;
}

/* -------------------------------------------------------------------- papers */

/** Every page of one paper.
 *
 *  The learner id is required rather than optional: the service made it so, and
 *  a call without it is refused rather than degraded. A stranger gets 404 and
 *  not 403, deliberately, so the answer does not confirm that a paper by that
 *  id exists. The client must not translate that into "the paper is gone" for
 *  the owner: `say.ts` keeps it at "that page is no longer on our server",
 *  which is true of both cases from where a parent is standing. */
export async function getPaper(paperId: string,
                               childId: string): Promise<PaperState> {
  const res = await ask(`${BASE}/v1/papers/${encodeURIComponent(paperId)}`,
    { headers: { "X-Learner": childId } });
  if (!res.ok) throw await refusal(res);
  return (await res.json()) as PaperState;
}

/* -------------------------------------------------------------------- thread */

/** The learner id travels in a header, never in the path or the query. It is a
 *  bearer capability, and a query string is written to access logs, kept in
 *  browser history and leaked in a Referer. */
export async function thread(childId: string, limit = 50): Promise<Thread> {
  const res = await ask(`${BASE}/v1/thread?limit=${limit}`,
    { headers: { "X-Learner": childId } });
  if (!res.ok) throw await refusal(res);
  return (await res.json()) as Thread;
}

/* ----------------------------------------------------------------- attention */

/**
 * What the service says might need this parent's attention, or null.
 *
 * **Null on 404, and 404 is the ordinary answer.** The route does not exist
 * unless the operator has turned `EDUPIPE_ATTENTION` on, and it is off:
 * `UX.md` section 8 row 4 puts a comprehension gate in front of it that needs
 * three parents and has not happened. So the screen has to treat absence as
 * normal rather than as a fault, and this returns null rather than throwing
 * for it. Every other refusal throws, because a 500 here is a real problem and
 * swallowing it would show a parent a screen that quietly stopped working.
 *
 * The client does no gating of its own. The three gates are counts over
 * findings and tutor corrections, and the service is the only side that holds
 * either; a second rule here would be a second thing to keep true and would
 * disagree first.
 */
export async function attention(childId: string): Promise<Attention | null> {
  const res = await ask(`${BASE}/v1/attention`, { headers: { "X-Learner": childId } });
  if (res.status === 404) return null;
  if (!res.ok) throw await refusal(res);
  return (await res.json()) as Attention;
}

/** Put one item away, or bring it back. Per learner and reversible, which is
 *  what the service's own route is: a list that only grows is ignored inside a
 *  fortnight. */
export async function dismissAttention(childId: string, ref: string,
                                       restore = false): Promise<boolean> {
  const res = await ask(`${BASE}/v1/attention/dismiss`, {
    method: "POST",
    headers: { "X-Learner": childId, "Content-Type": "application/json" },
    body: JSON.stringify({ ref, restore }),
  });
  return res.ok;
}

/* ------------------------------------------------------------------ practice */

/** Where a rendered practice page is served from.
 *
 *  The report carries a filename rather than a URL, because the service returns
 *  its pipeline output verbatim and that object has no idea which origin is
 *  rendering it. Composing the two is the client's job and this is the one
 *  place it happens. */
export function practiceUrl(image: string): string {
  return `${BASE}/v1/practice/${encodeURIComponent(image)}`;
}

/* ------------------------------------------------------------------ the poll */

/** How long a poll keeps trying through a connection that is not there. Two
 *  minutes covers a lift, a tunnel and a wifi handover; past that a parent is
 *  owed a screen that says what happened rather than a stage list that never
 *  advances. */
export const RETRY_WINDOW_MS = 120_000;

/** The connection is down and the poll is still trying. Null once it is back. */
export interface Trouble {
  /** When it first failed, and it has not succeeded since. */
  since: number;
  attempts: number;
}

export interface PollOptions {
  intervalMs?: number;
  timeoutMs?: number;
  retryWindowMs?: number;
  /** The durable handle. With it, a service that restarted mid-read still
   *  answers: the job map is process-local and dies with the process, while
   *  the report is stored against the hash of the page's bytes. Without it, a
   *  404 on the job ends the poll. */
  sha256?: string;
  /** Called on every failed round and once with null on recovery, so a screen
   *  can say "still trying" instead of showing three stages that have stopped
   *  moving with no explanation. */
  onTrouble?: (trouble: Trouble | null) => void;
  /** Stops the poll at the next round. A screen that unmounts sets this.
   *
   *  An aborted poll rejects with a ServiceError at `UNREACHED`, which is the
   *  same shape a dropped connection has. That is fine because the only caller
   *  who aborts is the one who has already gone, and it must check
   *  `signal.aborted` before it puts any sentence on a screen: `say()` would
   *  otherwise tell a parent the connection failed when nothing did. */
  signal?: AbortSignal;
}

/** A page the poll recovered from the stored-report route after the job died.
 *
 *  Its own type rather than a `PageDone`, because a `PageDone` carries an
 *  `Entitlement` and this route does not answer with one. Synthesising a meter
 *  here would be the client inventing numbers about a parent's money, which is
 *  the one thing no file in `src/lib` is allowed to do. `entitlement: null`
 *  makes a screen handle the absence before it can print a balance. */
export interface RestoredPage {
  job_id: string;
  state: "done";
  cached: true;
  billable: boolean | null;
  report: Report;
  /** Carried across from the stored-report route, which answers it per
   *  learner. A job lost to a restart still lands on a screen that has to
   *  decide whether to draw the confirm control. FP1-05. */
  can_confirm: boolean | null;
  entitlement: null;
}

/** What the poll reports. Everything the service can answer, plus the one
 *  state the poll itself can reach. */
export type PolledState = PageState | RestoredPage;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** A page that settled, counted once with the state a parent was shown.
 *
 *  Agent V5-C, wave 2. The outcome name is the app's own seven-state word and
 *  nothing else goes with it: no hash, no learner, no tier, no marks. It is
 *  counted where the poll settles rather than on the screen, so a parent who
 *  walks away from the reading screen is still one read. */
function countRead(settled: PolledState): void {
  if (settled.state !== "done" && settled.state !== "error") return;
  track("page read", { outcome: outcomeOf(settled) });
}

/** Slower each time, capped. A phone with no signal answering every 900ms
 *  spends battery to learn the same thing it learnt a second ago. */
function backoff(attempts: number, base: number): number {
  return Math.min(base * 2 ** Math.min(attempts - 1, 6), 10_000);
}

/** Poll one job until it settles, reporting every state on the way.
 *
 *  **A dropped connection is not the end of the page.** Anything `isTransient`
 *  recognises is retried with a widening gap for two minutes, and the caller is
 *  told it is happening so the screen can say so. Only when that window is
 *  spent does this throw, and it throws with a status the caller can name
 *  rather than a browser string. A terminal failure throws at once: waiting
 *  does not fix a 403.
 *
 *  **A 404 on the job is not the end of it either**, when `sha256` is known.
 *  The stored report is asked next, and only when that is empty too is the page
 *  reported lost. Without this, restarting the service during a read turned a
 *  finished report into a dead poll.
 *
 *  Answers the settled state. `onState` sees every state including the queued
 *  and running ones, so a processing screen can move.
 */
export async function poll(
  jobId: string,
  childId: string | undefined,
  onState: (state: PolledState) => void,
  { intervalMs = 900, timeoutMs = 900_000, retryWindowMs = RETRY_WINDOW_MS,
    sha256, onTrouble, signal }: PollOptions = {},
): Promise<PolledState> {
  const deadline = Date.now() + timeoutMs;
  let troubleSince: number | null = null;
  let attempts = 0;

  for (;;) {
    if (signal?.aborted) throw new ServiceError("the poll was stopped", UNREACHED);

    let state: PageState;
    try {
      state = await getPage(jobId, childId);
    } catch (err) {
      if (isTransient(err)) {
        if (troubleSince === null) troubleSince = Date.now();
        attempts += 1;
        onTrouble?.({ since: troubleSince, attempts });
        if (Date.now() - troubleSince > retryWindowMs) {
          throw new ServiceError("the connection did not come back", UNREACHED);
        }
        await sleep(backoff(attempts, intervalMs));
        continue;
      }
      if (!(err instanceof ServiceError) || err.status !== 404 || !sha256) throw err;

      /* The job is gone. The report may not be. */
      let stored: Stored | null;
      try {
        stored = await getReport(sha256, childId);
      } catch (second) {
        if (!isTransient(second)) throw second;
        if (troubleSince === null) troubleSince = Date.now();
        attempts += 1;
        onTrouble?.({ since: troubleSince, attempts });
        if (Date.now() - troubleSince > retryWindowMs) {
          throw new ServiceError("the connection did not come back", UNREACHED);
        }
        await sleep(backoff(attempts, intervalMs));
        continue;
      }
      const settled: PolledState = stored
        ? {
            job_id: jobId, state: "done", cached: true,
            billable: stored.billable, report: stored.report,
            can_confirm: stored.can_confirm,
            entitlement: null,
          }
        : {
            job_id: jobId, state: "error", billable: false, quality: null,
            error: {
              kind: "lost",
              detail: "the service restarted before this page was read",
            },
          };
      onState(settled);
      countRead(settled);
      return settled;
    }

    if (troubleSince !== null) {
      troubleSince = null;
      attempts = 0;
      onTrouble?.(null);
    }
    onState(state);
    if (state.state === "done" || state.state === "error") {
      countRead(state);
      return state;
    }
    if (Date.now() > deadline) {
      throw new ServiceError("this page is taking longer than expected", 504);
    }
    await sleep(intervalMs);
  }
}

/* ------------------------------------------------------------------ accounts
 *
 * Six routes, all under `/v1/account`, and the session header goes on these and
 * on nothing else. A page upload, a poll, a thread read and a report fetch
 * carry `X-Learner` and no session, so the bearer token for a parent's account
 * never travels with a photograph of their child's work.
 *
 * **Accounts can be off**, which is a host setting, and the app then shows no
 * account controls at all. The two answers that separate the cases are 404
 * ("there are no accounts on this service") and 401 ("there are, and you are
 * not signed in"), which is why `accountState` reports its outcome rather than
 * throwing on both.
 *
 * **The account comes in an envelope.** `GET /v1/account` and
 * `POST /v1/account/learners` both answer `{"account": {...}}`, and
 * `POST /v1/account/verify` answers `{"session": "...", "account": {...}}`.
 * The old client read only `verify` that way, nothing threw, and a parent who
 * signed in and reopened the app was shown no account at all with a live
 * session in storage the whole time. No test could have found it: the mock
 * answered unwrapped too, so the mock and the client were wrong together and
 * agreed.
 */

/** What a probe of `GET /v1/account` found.
 *
 *  Four states rather than two, and `unknown` is the one worth naming: a probe
 *  that never reached the service is not a service with accounts turned off,
 *  and treating it as one would be the client inventing an answer. Nothing is
 *  shown in either case, so a parent never meets a sign-in control that cannot
 *  work; the difference is that `unknown` is asked again on the next launch and
 *  `off` is a fact about the host. */
export type AccountState =
  | { state: "off" }
  | { state: "out" }
  | { state: "in"; account: Account }
  | { state: "unknown" };

function sessioned(token: string,
                   extra?: Record<string, string>): RequestInit {
  return { headers: { ...extra, "X-Session": token } };
}

function whole(held: Account | undefined): Account | null {
  return typeof held?.email === "string"
    ? { ...held, learners: held.learners ?? [] }
    : null;
}

/** Probe, once per launch. With a session it also answers who is signed in, so
 *  the launch costs one request rather than two. It never throws, for the same
 *  reason `entitlement` never throws. */
export async function accountState(token?: string): Promise<AccountState> {
  try {
    const res = await ask(`${BASE}/v1/account`,
      token ? sessioned(token) : undefined);
    if (res.status === 404) return { state: "off" };
    if (res.status === 401) return { state: "out" };
    if (!res.ok) return { state: "unknown" };
    const body = (await res.json()) as { account?: Account };
    const account = whole(body?.account);
    return account ? { state: "in", account } : { state: "unknown" };
  } catch {
    return { state: "unknown" };
  }
}

/** What asking for a code produced. `ttl` is the service's own `ttl_seconds`,
 *  in seconds, or null from a service that did not say. */
export type CodeSent =
  | { state: "sent"; ttl: number | null }
  /* Agent V5-C, wave 2. Under EDUPIPE_SIGNUP=invite an address with no invite
     is answered 202 with `waiting: true` and written to the waiting list. It
     is a 202 like any other, so nothing about it says whether the address has
     an account: what it says is that no code was sent to it now. */
  | { state: "waiting" }
  | { state: "off" };

/** Ask for a six-digit code.
 *
 *  202 for every address the service will send to, whether or not it has an
 *  account, which is the whole of the no-enumeration rule: a parent typing a
 *  colleague's address learns nothing about whether they are a customer. 404 is
 *  the host with accounts off, and it is the only answer that means anything
 *  different.
 *
 *  **`ttl_seconds` comes back with it.** The code screen used to state ten
 *  minutes, which is a claim about a service constant no client can see: true
 *  on the day it was written and silently wrong the day `EDUPIPE_CODE_TTL`
 *  moves. Null where the service did not send one, and the screen then says
 *  nothing about how long. */
export async function requestCode(email: string): Promise<CodeSent> {
  const res = await ask(`${BASE}/v1/account/code`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (res.status === 404) return { state: "off" };
  if (res.status !== 202) throw await refusal(res);
  let ttl: number | null = null;
  try {
    const body = (await res.json()) as {
      ttl_seconds?: unknown; waiting?: unknown; sent?: unknown;
    };
    /* The waiting answer is read off `waiting` and not off `sent`, and only a
       true reads as one. A service that sends neither field is the open
       service this app has always talked to. */
    if (body?.waiting === true) return { state: "waiting" };
    if (typeof body?.ttl_seconds === "number" && body.ttl_seconds > 0) {
      ttl = body.ttl_seconds;
    }
  } catch {
    /* A 202 with no body, or one this cannot parse. The code was still sent;
       only the sentence about how long is lost. */
  }
  return { state: "sent", ttl };
}

/** Type the code. Null is every failure there is.
 *
 *  The service answers one identical 401 for a wrong code, an expired code, a
 *  code for another address and an address that was never sent one, so this
 *  cannot tell them apart and must not pretend to. The screen says the code did
 *  not work and says nothing about the email. */
export async function verifyCode(email: string, code: string):
    Promise<{ session: string; account: Account } | null> {
  const res = await ask(`${BASE}/v1/account/verify`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email, code }),
  });
  if (res.status === 401) return null;
  if (!res.ok) throw await refusal(res);
  const body = (await res.json()) as { session?: string; account?: Account };
  const account = whole(body?.account);
  return body?.session && account ? { session: body.session, account } : null;
}

/** Attach one learner already on this phone to the account: the id, the year,
 *  and now the name.
 *
 *  **The name is sent here, and here is the whole of where it goes.** Until V3
 *  it was written on the phone it was typed on and never left, and the cost of
 *  that was a second phone that knew there were two children and could not say
 *  which was which. The service stores it as ciphertext under a key from its
 *  own environment and hands it back only to a verified session. Every other
 *  route in this file still carries the id alone.
 *
 *  It is a set rather than an insert, which is why the owed queue drains
 *  through this call rather than through `patchAccountLearner`: a learner
 *  minted while the phone had no signal has never been attached at all, and a
 *  PATCH at that id would answer 404. Answers the whole account object, so the
 *  caller holds one truth rather than patching a local copy. */
export async function attachLearner(token: string, childId: string,
                                    schoolYear?: string,
                                    name?: string,
                                    nation?: string): Promise<Account> {
  const res = await ask(`${BASE}/v1/account/learners`, {
    method: "POST",
    ...sessioned(token, { "content-type": "application/json" }),
    body: JSON.stringify({
      child_id: childId,
      school_year: schoolYear ?? null,
      /* Sent only when a parent chose a nation. Absent means England on the
         service too, so the body a phone always sent is unchanged. */
      ...(nation && nation !== "england" ? { nation } : {}),
      /* Absent rather than null when there is nothing to say. A null would be
         the phone asking the account to forget a name, and a phone that simply
         has not been told one must not do that to the other phone that has. */
      ...(name && name.trim() ? { name: name.trim() } : {}),
    }),
  });
  if (!res.ok) throw await refusal(res);
  const body = (await res.json()) as { account?: Account };
  const account = whole(body?.account);
  if (!account) throw new ServiceError("no account in the answer", res.status);
  return account;
}

/** Change one field on a learner the account already holds: the name, the
 *  school year, or both.
 *
 *  The fast path for the edit sheet, where the parent is signed in, the
 *  learner is on the account and the change is one field. It answers the whole
 *  account, the same as the attach.
 *
 *  **A failure here is not reported to the parent and is not lost either.**
 *  The caller writes the change to the owed queue and the attach drains it on
 *  the next launch, because the change a parent made is already on this phone
 *  and the only thing waiting is the account's copy of it. */
export async function patchAccountLearner(
  token: string, childId: string,
  patch: {
    name?: string; school_year?: string; nation?: string | null;
    exam_board?: string | null;
    /** "foundation", "higher", or null to clear it. `tier` on the wire and
     *  `exam_tier` in the service's column, which calls the report letter a
     *  tier already. */
    tier?: string | null;
  },
): Promise<Account> {
  const res = await ask(
    `${BASE}/v1/account/learners/${encodeURIComponent(childId)}`,
    {
      method: "PATCH",
      ...sessioned(token, { "content-type": "application/json" }),
      body: JSON.stringify(patch),
    });
  if (!res.ok) throw await refusal(res);
  const body = (await res.json()) as { account?: Account };
  const account = whole(body?.account);
  if (!account) throw new ServiceError("no account in the answer", res.status);
  return account;
}

/** Sign out, and whether the server confirmed it.
 *
 *  It still swallows every failure: the token is dropped from this device
 *  whatever the service says, because a parent who presses "Sign out" on a
 *  phone with no signal and stays signed in has been ignored.
 *
 *  **It now reads the status, and answers it.** The old version awaited the
 *  request inside a try and inspected nothing, so a 500, a refusal and a
 *  dropped call were all indistinguishable from a revoked session, and the
 *  screen had no way to avoid implying the stronger one. True means the
 *  service ended the session, or told us there was none to end. False means
 *  the session row is still there and will expire on its own, and the screen
 *  says only that this phone has forgotten the sign-in. */
export async function signOut(token: string): Promise<boolean> {
  try {
    const res = await ask(`${BASE}/v1/account/session`,
      { method: "DELETE", ...sessioned(token) });
    /* 401 is a session the service does not hold, which is the state a sign
       out is asking for. 404 is a service with accounts off, where there is
       no session row anywhere. */
    return res.ok || res.status === 401 || res.status === 404;
  } catch {
    return false;
  }
}

/** Delete the account: the email and the link between phones.
 *
 *  `learners_kept` is the service's own count of what it did NOT delete, and
 *  the screen states it, because the one thing a parent needs to be sure of
 *  here is that their children's pages are still there. Removing those is the
 *  per-learner control and it is deliberately somewhere else. */
export async function deleteAccount(token: string): Promise<AccountDeleted> {
  const res = await ask(`${BASE}/v1/account`,
    { method: "DELETE", ...sessioned(token) });
  if (!res.ok) throw await refusal(res);
  const body = (await res.json()) as Partial<AccountDeleted>;
  /* Fail closed. A 2xx whose body has lost its shape used to become
     `{deleted: true, learners_kept: 0}`, which told a parent their account was
     gone and their children's pages with it, on no evidence at all. Deletion is
     the one answer this client must not fill in for the service. */
  if (typeof body?.deleted !== "boolean" || typeof body?.learners_kept !== "number") {
    throw new ServiceError("the service did not say what it deleted", res.status);
  }
  return {
    deleted: body.deleted,
    learners_kept: body.learners_kept,
    rows: body.rows,
    detail: body.detail,
  };
}

/* ---------------------------------------------- Agent V5-C, wave 2: /v1/admin
 *
 * The operator's own screen: invites, the waiting list, grants, the free-tier
 * settings, the outcome mix, the spend against the cap and the tutor queue.
 *
 * **The session header goes on these too, and this is the second half of the
 * product that carries it.** The rule the file header states is unchanged in
 * substance: `sessioned()` is still the only thing that writes `X-Session`,
 * and it is still never written by anything on the learner half. What has
 * grown is the list of routes that ask for one, from `/v1/account` to
 * `/v1/account` and `/v1/admin`.
 *
 * **404 rather than 403 for everybody else**, which the service does on
 * purpose so the routes do not exist for a parent who is not an admin. This
 * client answers the same way it would to a route that is not there: null, or
 * nothing, never an error a screen would put in front of somebody.
 */

/** A GET under /v1/admin, or null where the answer was not one. */
async function adminGet<T>(token: string, path: string): Promise<T | null> {
  try {
    const res = await ask(`${BASE}/v1/admin/${path}`, sessioned(token));
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

/** A write under /v1/admin. True when the service did it. */
async function adminWrite(token: string, path: string, method: string,
                          body?: unknown): Promise<boolean> {
  try {
    const res = await ask(`${BASE}/v1/admin/${path}`, {
      method,
      ...sessioned(token, body ? { "content-type": "application/json" } : undefined),
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function adminOverview(token: string): Promise<AdminOverview | null> {
  return adminGet<AdminOverview>(token, "overview");
}

/** The invited addresses. An empty list and a refusal look the same from here
 *  and the screen says the same thing about both: nothing to show. */
export async function adminInvites(token: string): Promise<Invite[]> {
  const body = await adminGet<{ invites?: Invite[] } | Invite[]>(token, "invites");
  if (Array.isArray(body)) return body;
  return body?.invites ?? [];
}

/** Permit an address. It sends nothing to anybody: an invite is permission,
 *  and the parent still asks for their own code. */
export async function adminInvite(token: string, email: string,
                                  note?: string): Promise<boolean> {
  return adminWrite(token, "invites", "POST", { email, note: note || null });
}

/** Withdraw one. The address goes in the body rather than in the path or the
 *  query: a query string is written to access logs and kept in history, and
 *  this one is somebody's email address. */
export async function adminRevokeInvite(token: string,
                                        email: string): Promise<boolean> {
  return adminWrite(token, "invites", "DELETE", { email });
}

/** Have the service mail one invited address the invitation.
 *
 *  **The path carries the row's id and never the address**, which is the same
 *  rule `adminRevokeInvite` above keeps by putting the address in a body: a URL
 *  is written to every access log, kept in browser history and leaked in a
 *  Referer. There is no body at all, so this request chooses no recipient: the
 *  service mails whichever address that id already names on its invite list.
 *
 *  The status is what comes back rather than a boolean, because the four
 *  refusals are four different things for an operator to do next and
 *  `screens/admin/invite.ts` turns each into its own sentence.
 */
export async function adminSendInvite(token: string,
                                      id: string): Promise<InviteSend> {
  try {
    const res = await ask(
      `${BASE}/v1/admin/invites/${encodeURIComponent(id)}/send`,
      { method: "POST", ...sessioned(token) });
    if (res.ok) return "sent";
    if (res.status === 404) return "gone";
    if (res.status === 409) return "account";
    if (res.status === 429) return "recent";
    return "failed";
  } catch {
    return "failed";
  }
}

export async function adminWaiting(token: string): Promise<Waiting[]> {
  const body = await adminGet<{ waiting?: Waiting[] } | Waiting[]>(token, "waiting");
  if (Array.isArray(body)) return body;
  return body?.waiting ?? [];
}

/** Give one learner credits, as a pilot grant. */
export async function adminGrant(token: string, childId: string,
                                 credits: number, note?: string): Promise<boolean> {
  return adminWrite(token, "grants", "POST",
    { child_id: childId, credits, note: note || null });
}

/* ------------------------------------------- the settings whitelist, wave 2b
 *
 * The service holds the whitelist, states the label, the type, the bounds and
 * the last change for every key, and refuses anything outside them. This app
 * holds no list of keys at all. What these two functions add is the
 * translation between the service's vocabulary and the screen's, in one place,
 * because the alternative found at the wave 2b merge was two of them: W2b-S
 * answers a map of rows typed `int`, `bool`, `text`, `email`, `sources` and
 * `links`, and W2b-C's screen was built for a list of rows typed `number`,
 * `boolean`, `text` and `choice`. Neither side was wrong about its own half
 * and the contract rule is that the service wins, so the service's shape is
 * what arrives and this is where it is read.
 *
 * A type this app has no control for keeps its own name and the screen prints
 * it rather than editing it. That is the safe direction: a new type on the
 * service appears as a value somebody can read, never as a text box that would
 * write a broken one.
 */

/** The service's type names, in the screen's vocabulary. Anything not here
 *  keeps its own name and gets the read-only treatment. */
const SETTING_TYPES: Record<string, string> = {
  int: "number",
  bool: "boolean",
  choice: "choice",
  text: "text",
  /* An address is a line of text with a rule about its shape, and the rule is
     the service's to apply. A box that refused one here would be a second
     opinion about what an address is. */
  email: "text",
};

/** One row as the service sent it, read into the row the screen draws.
 *
 *  `at` arrives as a float of unix seconds, which is what every other time on
 *  this wire is, and leaves as an ISO string, which is what `SettingChange`
 *  holds and what `new Date` reads without a second thought about units. */
function settingRow(held: Record<string, unknown>): SettingRow | null {
  const key = held.key;
  const label = held.label;
  const kind = held.type;
  if (typeof key !== "string" || typeof label !== "string"
      || typeof kind !== "string") return null;

  const bounds = (held.bounds ?? {}) as Record<string, unknown>;
  const change = held.last_change as
    { by?: unknown; at?: unknown; old?: unknown } | null | undefined;
  const at = typeof change?.at === "number"
    ? new Date(change.at * 1000).toISOString()
    : typeof change?.at === "string" ? change.at : null;

  return {
    key,
    label,
    type: SETTING_TYPES[kind] ?? kind,
    bounds: {
      min: typeof bounds.min === "number" ? bounds.min : null,
      max: typeof bounds.max === "number" ? bounds.max : null,
      /* The service says nothing about a step, because it has no need to: an
         `int` is a whole number by its own definition. The screen refuses a
         fraction before the request only where it has been told to, so the
         rule the type already carries is written out here. */
      step: kind === "int" ? 1 : null,
      max_length: typeof bounds.max_length === "number" ? bounds.max_length : null,
      choices: Array.isArray(bounds.choices)
        ? bounds.choices.map((c) => String(c)) : null,
    },
    value: held.value,
    last_change: change && typeof change.by === "string"
      ? { by: change.by, at, old: change.old } : null,
    hint: typeof held.help === "string" ? held.help : null,
  };
}

/** Every setting the service will let this admin change, in its own order.
 *
 *  Null is "we could not read them", which is not the same as "there are
 *  none": an empty list is a service that answered and offered nothing. The
 *  screen says a different sentence for each.
 *
 *  The map is the shape the service sends and the list is accepted beside it,
 *  because a body that is already a list of rows needs no unpacking and a
 *  reader that took only one of the two would break on the other for no
 *  reason a parent or an operator could see. */
export async function adminSettingRows(token: string): Promise<SettingRow[] | null> {
  const body = await adminGet<Record<string, unknown> | unknown[]>(
    token, "settings");
  if (body === null) return null;
  const held = Array.isArray(body)
    ? body
    : (body as { settings?: unknown }).settings;
  const list = Array.isArray(held)
    ? held
    : held && typeof held === "object"
      ? Object.values(held as Record<string, unknown>)
      : null;
  if (list === null) return null;
  return list
    .map((row) => (row && typeof row === "object"
      ? settingRow(row as Record<string, unknown>) : null))
    .filter((row): row is SettingRow => row !== null);
}

/** Write one setting.
 *
 *  One key per request, which is a rule about the change log as much as about
 *  the screen: a request carrying four keys writes four log lines that share a
 *  moment and cannot be told apart afterwards, and a refusal of one of them
 *  leaves the operator guessing which of the four stands.
 *
 *  The body is the service's, `{settings: {key: value}}`, carrying exactly one
 *  key. W2b-C proposed `{key, value}` and said in its own report that the
 *  service owns the shape; it does, and this is the shape it takes. */
export async function adminSaveSetting(token: string, key: string,
                                       value: unknown): Promise<boolean> {
  return adminWrite(token, "settings", "PUT", { settings: { [key]: value } });
}

/** The tutor queue, for learners whose parent opted in. The service does that
 *  filtering; nothing here may widen it. */
export async function adminQueue(token: string): Promise<QueueEntry[]> {
  /* `items` is the key the route answers under (`api.py:8783`). This read
     `queue`, which nothing sends, so the admin screen's queue table could
     never fill against a real service. Both names are read, because a client
     that insists on one is how this happened. */
  const body = await adminGet<
    { items?: QueueEntry[]; queue?: QueueEntry[] } | QueueEntry[]>(
      token, "queue");
  if (Array.isArray(body)) return body;
  return body?.items ?? body?.queue ?? [];
}


/** Where a recognised Oak lesson sits in a nation's own syllabus.
 *
 *  England answers from Oak's own unit mapping, a publisher claim. Another
 *  nation answers through reviewed links, each carrying the reviewer's
 *  relation to the England statement. An empty list means nothing has been
 *  linked yet; it never means England's statements. */
export async function lessonSyllabus(slug: string, nation: string): Promise<{
  /** England names the claim kinds the list actually carries; another nation answers through links. */
  lesson_slug: string; nation: string; basis: "publisher_claim" | "reviewer_claim" | "mixed" | "reviewed_link";
  objectives: Array<{ objective_id: string; syllabus: string; reference: string; stage_label: string; statement: string;
    relation?: "same" | "narrower" | "broader" | "overlaps"; via_codes?: string[];
    practice: { status: "approved" | "topic_match"; path: string | null } }>;
}> {
  const res = await fetch(`${BASE}/v1/lesson-syllabus/${encodeURIComponent(slug)}?nation=${encodeURIComponent(nation)}`,
                          { headers: { accept: "application/json" } });
  if (!res.ok) throw await refusal(res);
  return res.json();
}
